import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarPlus,
  Edit3,
  Trash2,
  Loader2,
  Image as ImageIcon,
  X,
  Save,
  Search,
  Upload,
} from "lucide-react";
import { authHeaders, canEdit } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["experiences", "academy", "xr", "festivals", "community", "corporate"];
const DISPLAY_CATEGORIES = ["festival", "retreat", "workshop", "family", "xr", "nightlife"];
const BOOKING_TYPES = ["stripe", "acuity", "eventbrite", "enquiry"];
const AVAILABILITY = ["open", "low", "sold_out"];

const EMPTY = {
  id: "",
  title: "",
  category: "experiences",
  display_category: "",
  date: "",
  time: "",
  location: "",
  price: "",
  description: "",
  image: "",
  booking_type: "stripe",
  amount_gbp: 0,
  acuity_appointment_type: "",
  eventbrite_url: "",
  tag: "",
  availability: "open",
  long_description: "",
  schedule: [],
  accessibility: [],
  gallery: [],
  featured: false,
  featured_archive: false,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function EventsManager({ currentUser }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event object or null
  const [filter, setFilter] = useState("");

  const editorAllowed = canEdit(currentUser);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/events`, {
        headers: authHeaders(),
      });
      setEvents(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSave = async (payload, isNew) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/events`, payload, { headers: authHeaders() });
        toast.success(`Event published — ${payload.title}`);
      } else {
        await axios.put(`${API}/admin/events/${payload.id}`, payload, {
          headers: authHeaders(),
        });
        toast.success(`Event updated — ${payload.title}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/events/${ev.id}`, { headers: authHeaders() });
      toast.success("Event removed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = events.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      e.title.toLowerCase().includes(q) ||
      e.id.toLowerCase().includes(q) ||
      e.category.toLowerCase().includes(q)
    );
  });

  if (!editorAllowed) {
    return (
      <div
        data-testid="events-restricted"
        className="border border-amber-400/30 bg-zinc-950 p-8"
      >
        <h3 className="font-display font-bold text-2xl">Editor access required</h3>
        <p className="mt-3 text-zinc-300 text-sm leading-relaxed max-w-md">
          Only Editor or Owner accounts can manage events. Ask an Owner to
          upgrade your access from the Team tab.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="events-manager" className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border border-white/5 bg-zinc-950 p-4">
        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title, id or category…"
            data-testid="events-filter-input"
            className="flex-1 bg-transparent text-sm text-white focus:outline-none placeholder:text-zinc-600"
          />
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="events-create-button"
          className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold"
        >
          <CalendarPlus className="h-4 w-4" /> New event
        </button>
      </div>

      {/* List */}
      <div className="border border-white/5 bg-zinc-950 overflow-x-auto">
        <table className="w-full text-sm" data-testid="events-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
              <th className="text-left px-5 py-4">Event</th>
              <th className="text-left px-5 py-4">Category</th>
              <th className="text-left px-5 py-4">Date</th>
              <th className="text-left px-5 py-4">Booking</th>
              <th className="text-left px-5 py-4">Avail.</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center text-zinc-500 py-10">
                  <Loader2 className="h-5 w-5 animate-spin inline-block" />
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-zinc-500 py-10">
                  No events match.
                </td>
              </tr>
            )}
            {filtered.map((ev) => (
              <tr
                key={ev.id}
                data-testid={`events-row-${ev.id}`}
                className="border-b border-white/5 last:border-none"
              >
                <td className="px-5 py-4">
                  <p className="font-display font-bold inline-flex items-center gap-2 flex-wrap">
                    {ev.title}
                    {ev.featured && (
                      <span data-testid={`event-badge-featured-${ev.id}`} className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-300 border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5">
                        Featured
                      </span>
                    )}
                    {ev.featured_archive && (
                      <span data-testid={`event-badge-archive-${ev.id}`} className="text-[9px] uppercase tracking-[0.25em] font-bold text-emerald-300 border border-emerald-400/40 bg-emerald-400/10 px-1.5 py-0.5">
                        Archive
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-zinc-500">{ev.id}</p>
                </td>
                <td className="px-5 py-4 text-zinc-400 capitalize">
                  {ev.display_category || ev.category}
                </td>
                <td className="px-5 py-4 text-zinc-400">{ev.date} · {ev.time}</td>
                <td className="px-5 py-4">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-400">
                    {ev.booking_type}
                  </span>
                  {ev.amount_gbp != null && ev.booking_type === "stripe" && (
                    <p className="text-xs text-zinc-500">£{Number(ev.amount_gbp).toFixed(2)}</p>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
                      ev.availability === "sold_out"
                        ? "text-red-400"
                        : ev.availability === "low"
                          ? "text-amber-400"
                          : "text-emerald-400"
                    }`}
                  >
                    {ev.availability}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setEditing({ ...ev })}
                      data-testid={`events-edit-${ev.id}`}
                      className="text-amber-400 hover:text-amber-300"
                      title="Edit"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(ev)}
                      data-testid={`events-delete-${ev.id}`}
                      className="text-red-400 hover:text-red-300"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EventDrawer
          initial={editing}
          isNew={!events.some((e) => e.id === editing.id)}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

// ---------- Drawer (create / edit) ----------
function EventDrawer({ initial, isNew, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setField("image", fullUrl);
      toast.success("Image uploaded.");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      id: form.id?.trim() || slugify(form.title),
      amount_gbp:
        form.booking_type === "stripe"
          ? Number(form.amount_gbp || 0)
          : null,
    };
    await onSave(payload, isNew);
    setBusy(false);
  };

  return (
    <div
      data-testid="event-drawer"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-end"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full sm:w-[640px] h-full overflow-y-auto bg-zinc-950 border-l border-amber-400/20 p-7 pb-32"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-bold text-2xl">
            {isNew ? "New event" : `Edit · ${initial.title}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="event-drawer-close"
            className="opacity-60 hover:opacity-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <TextField label="Title" value={form.title} onChange={(v) => setField("title", v)} required testId="event-field-title" />
          <TextField
            label="ID (slug)"
            value={form.id}
            onChange={(v) => setField("id", v)}
            disabled={!isNew}
            placeholder={slugify(form.title) || "auto-generated"}
            testId="event-field-id"
          />
          <SelectField label="Category (internal)" value={form.category} onChange={(v) => setField("category", v)} options={CATEGORIES} testId="event-field-category" />
          <SelectField
            label="Public filter chip"
            value={form.display_category || ""}
            onChange={(v) => setField("display_category", v)}
            options={["", ...DISPLAY_CATEGORIES]}
            testId="event-field-display-category"
          />
          <div className="grid grid-cols-2 gap-3">
            <ToggleField
              label="Featured on homepage"
              value={!!form.featured}
              onChange={(v) => setField("featured", v)}
              testId="event-field-featured"
            />
            <ToggleField
              label="Keep visible after date (archive)"
              value={!!form.featured_archive}
              onChange={(v) => setField("featured_archive", v)}
              testId="event-field-featured-archive"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Date" value={form.date} onChange={(v) => setField("date", v)} placeholder="2026-06-12" testId="event-field-date" />
            <TextField label="Time" value={form.time} onChange={(v) => setField("time", v)} placeholder="19:00" testId="event-field-time" />
          </div>
          <TextField label="Location" value={form.location} onChange={(v) => setField("location", v)} testId="event-field-location" />
          <TextField label="Price (display)" value={form.price} onChange={(v) => setField("price", v)} placeholder="£20 · £15 conc." testId="event-field-price" />

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Image URL</label>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={form.image}
                onChange={(e) => setField("image", e.target.value)}
                data-testid="event-field-image"
                className="flex-1 bg-black border border-white/10 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400"
                placeholder="https://… or upload below"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="event-image-upload"
                className="inline-flex items-center gap-2 border border-amber-400/40 hover:border-amber-400 text-amber-400 px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-bold"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload
              </button>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                ref={fileRef}
                onChange={onImagePick}
                className="hidden"
              />
            </div>
            {form.image && (
              <div className="mt-3 border border-white/5 p-3 inline-block">
                <img
                  src={form.image}
                  alt="Preview"
                  className="max-h-32 object-cover"
                />
              </div>
            )}
          </div>

          <SelectField label="Booking type" value={form.booking_type} onChange={(v) => setField("booking_type", v)} options={BOOKING_TYPES} testId="event-field-booking-type" />

          {form.booking_type === "stripe" && (
            <TextField
              label="Amount (£)"
              value={form.amount_gbp}
              onChange={(v) => setField("amount_gbp", v)}
              type="number"
              step="0.01"
              testId="event-field-amount"
            />
          )}
          {form.booking_type === "acuity" && (
            <TextField
              label="Acuity appointment type"
              value={form.acuity_appointment_type || ""}
              onChange={(v) => setField("acuity_appointment_type", v)}
              testId="event-field-acuity-type"
            />
          )}
          {form.booking_type === "eventbrite" && (
            <TextField
              label="Eventbrite URL"
              value={form.eventbrite_url || ""}
              onChange={(v) => setField("eventbrite_url", v)}
              testId="event-field-eventbrite-url"
            />
          )}

          <TextField label="Short description" value={form.description} onChange={(v) => setField("description", v)} testId="event-field-description" />
          <TextAreaField label="Long description (optional)" value={form.long_description || ""} onChange={(v) => setField("long_description", v)} testId="event-field-long-description" />

          <SelectField label="Availability" value={form.availability} onChange={(v) => setField("availability", v)} options={AVAILABILITY} testId="event-field-availability" />
          <TextField label="Tag (optional)" value={form.tag || ""} onChange={(v) => setField("tag", v)} placeholder="flagship, last-tickets…" testId="event-field-tag" />
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="event-drawer-save"
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? "Publish event" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Form atoms ----------
function TextField({ label, value, onChange, type = "text", placeholder, required, disabled, testId, step }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        step={step}
        data-testid={testId}
        className="mt-2 w-full bg-black border border-white/10 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 disabled:opacity-60"
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</label>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={4}
        className="mt-2 w-full bg-black border border-white/10 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-black border border-white/10 px-3 py-3 text-white focus:outline-none focus:border-amber-400"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o === "" ? "(none)" : o}</option>
        ))}
      </select>
    </div>
  );
}

function ToggleField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">
        {label}
      </label>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testId}
        aria-pressed={value}
        className={`mt-2 w-full inline-flex items-center justify-between gap-2 px-3 py-3 border text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
          value
            ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
            : "border-white/10 bg-black text-zinc-400"
        }`}
      >
        <span>{value ? "On" : "Off"}</span>
        <span
          className={`relative inline-flex h-5 w-9 items-center transition-colors ${
            value ? "bg-amber-400" : "bg-zinc-700"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 bg-black transition-transform ${
              value ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </span>
      </button>
    </div>
  );
}
